import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import { useOperationLogs } from "./useOperationLogs";
import { triggerProductionPrint } from "@/lib/public-order-printing";
import { logger } from "@/lib/logger";
import { safeRemoveChannel } from "@/lib/supabase-channel";
import type { Database } from "@/integrations/supabase/types";

type Order = Database["public"]["Tables"]["orders"]["Row"];
type OrderStatus = Database["public"]["Enums"]["order_status"];

export interface DeliveryOrderWithDetails extends Order {
  items: {
    id: string;
    product_id: string | null;
    product_name: string;
    quantity: number;
    unit_price: number;
    total_price: number;
    notes: string | null;
  }[];
  payments: {
    id: string;
    amount: number;
    fee_amount: number | null;
    payment_method: {
      id: string;
      name: string;
    } | null;
  }[];
}

export function useDeliveryOrders() {
  const { activeRestaurant } = useRestaurantContext();
  const queryClient = useQueryClient();
  const { logOperation } = useOperationLogs();

  // Fetch all active delivery orders (with delivery_address or order_type = delivery)
  const {
    data: orders = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["delivery-orders", activeRestaurant?.id],
    queryFn: async () => {
      if (!activeRestaurant) return [];

      const { data, error } = await supabase
        .from("orders")
        .select(`
          *,
          items:order_items(id, product_id, product_name, quantity, unit_price, total_price, notes),
          payments:order_payments(id, amount, fee_amount, payment_method:payment_methods(id, name))
        `)
        .eq("restaurant_id", activeRestaurant.id)
        .not("delivery_address", "is", null)
        .in("status", ["pending", "preparing", "ready", "out_for_delivery", "delivered"])
        .gte("created_at", new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()) // Last 24 hours
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as DeliveryOrderWithDetails[];
    },
    enabled: !!activeRestaurant,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Subscribe to realtime updates
  useEffect(() => {
    if (!activeRestaurant) return;

    const channel = supabase
      .channel("delivery-orders-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "orders",
          filter: `restaurant_id=eq.${activeRestaurant.id}`,
        },
        (payload) => {
          const order = payload.new as Order;
          // Only care about delivery orders
          if (order?.delivery_address) {
            logger.log("Delivery order realtime update:", payload.eventType);
            queryClient.invalidateQueries({ queryKey: ["delivery-orders"] });
          }
        }
      )
      .subscribe();

    return () => {
      safeRemoveChannel(channel);
    };
  }, [activeRestaurant, queryClient]);

  // Group orders by status
  const ordersByStatus = useMemo(() => {
    return {
      pending: orders.filter((o) => o.status === "pending"),
      preparing: orders.filter((o) => o.status === "preparing"),
      ready: orders.filter((o) => o.status === "ready"),
      out_for_delivery: orders.filter((o) => o.status === "out_for_delivery"),
      delivered: orders.filter((o) => o.status === "delivered"),
    };
  }, [orders]);

  // Accept/Confirm order (pending -> preparing)
  const confirmOrderMutation = useMutation({
    mutationFn: async (orderId: string) => {
      if (!activeRestaurant) throw new Error("No restaurant");

      const { error } = await supabase
        .from("orders")
        .update({ status: "preparing", updated_at: new Date().toISOString() })
        .eq("id", orderId);

      if (error) throw error;

      // Trigger production print with delivery flag
      await triggerProductionPrint(orderId, activeRestaurant.id);

      const order = orders.find((o) => o.id === orderId);
      // Send WhatsApp notification (skip iFood — iFood notifies the customer itself)
      if (order?.external_source !== "ifood") {
        supabase.functions
          .invoke("send-order-status-notification", {
            body: { order_id: orderId, notification_type: "order_confirmed" },
          })
          .catch((err) => logger.warn("Failed to send confirmation notification:", err));
      }
      await logOperation("update", "order", orderId, {
        order_number: order?.order_number,
        status_change: { old: "pending", new: "preparing" },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["delivery-orders"] });
      toast.success("Pedido confirmado! Enviado para produção.");
    },
    onError: () => {
      toast.error("Erro ao confirmar pedido");
    },
  });

  // Mark as ready (preparing -> ready)
  const markReadyMutation = useMutation({
    mutationFn: async (orderId: string) => {
      const { error } = await supabase
        .from("orders")
        .update({ status: "ready", updated_at: new Date().toISOString() })
        .eq("id", orderId);

      if (error) throw error;

      const order = orders.find((o) => o.id === orderId);
      await logOperation("update", "order", orderId, {
        order_number: order?.order_number,
        status_change: { old: "preparing", new: "ready" },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["delivery-orders"] });
      toast.success("Pedido marcado como pronto!");
    },
    onError: () => {
      toast.error("Erro ao marcar como pronto");
    },
  });

  // Send for delivery (ready -> out_for_delivery)
  const sendForDeliveryMutation = useMutation({
    mutationFn: async (orderId: string) => {
      if (!activeRestaurant) throw new Error("No restaurant");

      const { error } = await supabase
        .from("orders")
        .update({ status: "out_for_delivery", updated_at: new Date().toISOString() })
        .eq("id", orderId);

      if (error) throw error;

      const order = orders.find((o) => o.id === orderId);
      // Send WhatsApp notification (skip iFood)
      if (order?.external_source !== "ifood") {
        supabase.functions
          .invoke("send-order-status-notification", {
            body: { order_id: orderId, notification_type: "out_for_delivery" },
          })
          .catch((err) => logger.warn("Failed to send out for delivery notification:", err));
      }
      await logOperation("update", "order", orderId, {
        order_number: order?.order_number,
        status_change: { old: "ready", new: "out_for_delivery" },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["delivery-orders"] });
      toast.success("Pedido enviado para entrega!");
    },
    onError: () => {
      toast.error("Erro ao enviar para entrega");
    },
  });

  // Confirm delivery (out_for_delivery -> delivered) with optional payment
  const confirmDeliveryMutation = useMutation({
    mutationFn: async ({
      orderId,
      paymentMethodId,
      paymentAmount,
      alreadyPaid,
    }: {
      orderId: string;
      paymentMethodId?: string;
      paymentAmount?: number;
      alreadyPaid: boolean;
    }) => {
      if (!activeRestaurant) throw new Error("No restaurant");

      // Update order status
      const { error: orderError } = await supabase
        .from("orders")
        .update({ status: "delivered", updated_at: new Date().toISOString() })
        .eq("id", orderId);

      if (orderError) throw orderError;

      // Register payment if needed
      if (!alreadyPaid && paymentMethodId && paymentAmount && paymentAmount > 0) {
        const { error: paymentError } = await supabase.from("order_payments").insert({
          order_id: orderId,
          payment_method_id: paymentMethodId,
          amount: paymentAmount,
        });

        if (paymentError) throw paymentError;
      }

      const order = orders.find((o) => o.id === orderId);
      // Send WhatsApp notification (skip iFood)
      if (order?.external_source !== "ifood") {
        supabase.functions
          .invoke("send-order-status-notification", {
            body: { order_id: orderId, notification_type: "order_delivered" },
          })
          .catch((err) => logger.warn("Failed to send delivery notification:", err));
      }
      await logOperation("update", "order", orderId, {
        order_number: order?.order_number,
        status_change: { old: "out_for_delivery", new: "delivered" },
        payment: alreadyPaid ? "already_paid" : { method_id: paymentMethodId, amount: paymentAmount },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["delivery-orders"] });
      toast.success("Entrega confirmada!");
    },
    onError: () => {
      toast.error("Erro ao confirmar entrega");
    },
  });

  // Cancel delivery order
  const cancelOrderMutation = useMutation({
    mutationFn: async ({ orderId, reason }: { orderId: string; reason?: string }) => {
      const { error } = await supabase
        .from("orders")
        .update({ 
          status: "cancelled", 
          updated_at: new Date().toISOString(),
          notes: reason ? `Cancelado: ${reason}` : undefined,
        })
        .eq("id", orderId);

      if (error) throw error;

      const order = orders.find((o) => o.id === orderId);
      await logOperation("cancel", "order", orderId, {
        order_number: order?.order_number,
        reason,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["delivery-orders"] });
      toast.success("Pedido cancelado");
    },
    onError: () => {
      toast.error("Erro ao cancelar pedido");
    },
  });

  return {
    orders,
    ordersByStatus,
    isLoading,
    refetch,
    confirmOrder: confirmOrderMutation.mutate,
    markReady: markReadyMutation.mutate,
    sendForDelivery: sendForDeliveryMutation.mutate,
    confirmDelivery: confirmDeliveryMutation.mutate,
    cancelOrder: cancelOrderMutation.mutate,
    isUpdating:
      confirmOrderMutation.isPending ||
      markReadyMutation.isPending ||
      sendForDeliveryMutation.isPending ||
      confirmDeliveryMutation.isPending ||
      cancelOrderMutation.isPending,
  };
}
